import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Order } from '@hero/schema';
import { getApiErrorMessage } from '@hero/api';
import { LOGISTI_APP } from '@hero/apps';
import { useGovernmentWaybillManager } from '@hero/web-hooks';
import { useOrderDestination } from '@hero/hooks-shared';
import { AlertCircleIcon, CircleCheckIcon, TruckIcon, UserIcon } from '@hero/web-icons';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';
import { Input } from '../../../ui/Input/Input';
import { Spinner } from '../../../app/Spinner';
import { RefDisplay } from '../../../app/Ref';
import { RouteArrowDisplay } from '../../../app/RouteArrowDisplay';
import { PriceInput } from '../../../app/PriceInput';
import { WeightEdit } from '../../../app/Weight';
import { VehiclePlateDisplay } from '../../../app/VehiclePlateDisplay';
import { OrderItemTypeSelect } from '../../Order/Item/OrderItemTypeSelect';
import { OrderPackingTypeSelect } from '../../Order/Item/OrderPackingTypeSelect';
import { VehicleName } from '../../Vehicle/VehicleName';
import { MemberEditModal } from '../../User/Modal';
import { VehicleEditModal } from '../../Vehicle/Modal';
import { AppConnectBrand } from '../AppConnectBrand';
import { BayanGoodTypeSelect } from '../BayanGoodTypeSelect';
import { WaybillEntityCard } from './WaybillEntityCard';

export interface GovernmentWaybillDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
}

/**
 * Issue an order's government waybill (وثيقة النقل) from one screen.
 *
 * ## Why the driver and truck are cards, not fields
 *
 * A وثيقة is a government record about a specific person driving a specific
 * truck, so the first thing someone issuing one needs is to see which person
 * and which truck are about to be named on it. The cards show that up front,
 * and anything Bayan objects to about either record is listed on the card that
 * owns it and fixed in that record's own form. The drawer used to re-implement
 * an ID field and a plate field inline, which meant two forms could write the
 * same plate and the truck's own form still showed the old one.
 *
 * The order-level values — load, fare, CR number — have no other form worth
 * sending the user to, so they stay inline here.
 *
 * ## Why nothing here has its own save button
 *
 * A per-section save would leave the drawer showing a stale list until it
 * re-fetched. With one submit there is no in-between state to keep fresh: the
 * issue response IS the new state. The two record forms are the exception, and
 * they re-read the blockers on save for exactly that reason.
 */
export const GovernmentWaybillDrawer: React.FC<GovernmentWaybillDrawerProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const { t } = useTranslation(['m-apps', 'c-main']);
  const { firstPickup, lastDropoff } = useOrderDestination(order.destinations);
  const [isDriverEditOpen, setIsDriverEditOpen] = useState(false);
  const [isVehicleEditOpen, setIsVehicleEditOpen] = useState(false);

  const manager = useGovernmentWaybillManager({
    order,
    isOpen,
    onSettled: result => {
      if (result.status === 'issued') {
        toast.success(t('m-apps:toast.success.waybill_issued'));
        onClose();
        return;
      }
      // A 200 with `connected: false` means no Org party to this order has a
      // Bayan account, which reads nothing like "Bayan rejected the load" —
      // saying so would send the user hunting for a problem with the freight.
      if (!result.connected) {
        toast.error(t('m-apps:toast.error.not_connected'));
        return;
      }
      // Anything else left blockers behind. They are already re-rendering as
      // cards and inputs below, so the drawer stays open on the work that
      // remains.
      if (result.lastError) toast.error(result.lastError);
    },
  });

  const {
    draft,
    setField,
    requirements,
    driverBlockers,
    vehicleBlockers,
    unfixableBlockers,
    refetchStatus,
    isIssued,
    isLoading,
    waybill,
    currency,
    canIssue,
    isSubmitting,
    submit,
    error,
  } = manager;

  const needsInlineInput =
    requirements.loadPrice ||
    requirements.crNumber ||
    requirements.goodType ||
    driverBlockers.length > 0 ||
    vehicleBlockers.length > 0 ||
    unfixableBlockers.length > 0;

  const isReady =
    !needsInlineInput &&
    Boolean(draft.itemType && draft.weight && draft.itemDescription.trim()) &&
    driverBlockers.length === 0 &&
    vehicleBlockers.length === 0 &&
    unfixableBlockers.length === 0;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex flex-col bg-white p-0 w-[520px] sm:max-w-[520px]">
          <SheetHeader className="space-y-3 border-b px-5 py-4 text-start">
            <AppConnectBrand
              name={t('m-apps:logisti.name')}
              logo={LOGISTI_APP.logo}
              subtitle={t('m-apps:waybill.subtitle')}
              bareLogo
            />
            <div className="flex items-center justify-between gap-3">
              <RouteArrowDisplay origin={firstPickup} destinations={[lastDropoff]} size="small" />
              <RefDisplay refMain={order.ref?.main} refShort={order.ref?.short} />
            </div>
            <SheetTitle className="sr-only">{t('m-apps:waybill.title')}</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner className="size-8 text-gray-400" />
              </div>
            ) : isIssued ? (
              <IssuedSummary waybill={waybill} />
            ) : (
              <div className="space-y-6">
                <div className="space-y-3">
                  <WaybillEntityCard
                    icon={UserIcon}
                    title={t('m-apps:waybill.form.driver.title')}
                    name={order.driver?.name}
                    detail={order.driver?.phone}
                    blockers={driverBlockers}
                    emptyLabel={t('m-apps:waybill.form.entity.no_driver')}
                    onOpen={() => setIsDriverEditOpen(true)}
                  />

                  <WaybillEntityCard
                    icon={TruckIcon}
                    title={t('m-apps:waybill.form.truck.title')}
                    name={
                      order.vehicle?.registration?.plate ? (
                        <VehiclePlateDisplay plate={order.vehicle.registration.plate} />
                      ) : undefined
                    }
                    detail={
                      order.vehicle?.classification?.type ? (
                        <VehicleName vehicle={order.vehicle.classification.type} />
                      ) : undefined
                    }
                    blockers={vehicleBlockers}
                    emptyLabel={t('m-apps:waybill.form.entity.no_vehicle')}
                    onOpen={() => setIsVehicleEditOpen(true)}
                  />
                </div>

                {isReady ? (
                  <div className="flex items-start gap-3 rounded-lg bg-emerald-50 p-4">
                    <CircleCheckIcon className="mt-0.5 size-5 shrink-0 text-emerald-600" />
                    <p className="text-[13px] leading-[20px] text-emerald-900">
                      {t('m-apps:waybill.form.ready_hint')}
                    </p>
                  </div>
                ) : null}

                <Section title={t('m-apps:waybill.form.load.title')}>
                  <Field label={t('m-apps:waybill.form.item_type.title')}>
                    <OrderItemTypeSelect
                      itemType={draft.itemType}
                      onMaterialChange={value => setField('itemType', value)}
                      placeholder={t('m-apps:waybill.form.item_type.placeholder')}
                    />
                  </Field>

                  <Field label={t('m-apps:waybill.form.packing.title')}>
                    <OrderPackingTypeSelect
                      packing={draft.packing}
                      counter={draft.quantity}
                      showCounter
                      onPackingChange={value => setField('packing', value)}
                      onCounterChange={value => setField('quantity', value)}
                      placeholder={t('m-apps:waybill.form.packing.placeholder')}
                    />
                  </Field>

                  <Field label={t('m-apps:waybill.form.item_description.title')}>
                    <Input
                      value={draft.itemDescription}
                      onChange={event => setField('itemDescription', event.target.value)}
                      placeholder={t('m-apps:waybill.form.item_description.placeholder')}
                    />
                  </Field>

                  <Field
                    label={t('m-apps:waybill.form.weight.title')}
                    hint={t('m-apps:waybill.form.weight.hint')}>
                    <WeightEdit
                      value={draft.weight}
                      onChange={value => setField('weight', value)}
                      unit="ton"
                    />
                  </Field>

                  {/* Bayan only *requires* a goods value when the account
                      delivers to the client, but it prints on the وثيقة either
                      way — so it is offered with the rest of the load rather
                      than appearing only once it blocks issuance. */}
                  <Field
                    label={t('m-apps:waybill.form.item_cost.title')}
                    hint={t(
                      requirements.itemCost
                        ? 'm-apps:waybill.form.item_cost.hint_required'
                        : 'm-apps:waybill.form.item_cost.hint',
                    )}>
                    <PriceInput
                      value={draft.itemCost}
                      onChange={value => setField('itemCost', value)}
                      currency={currency}
                    />
                  </Field>
                </Section>

                {/* Hero's item type describes the load in our vocabulary;
                    Bayan wants a node from its own catalogue, and for branches
                    like construction or petroleum no single Hero type picks
                    one. Narrowed to the branch so this is a short list. */}
                {requirements.goodType ? (
                  <Section title={t('m-apps:waybill.form.good_type.title')}>
                    <Field hint={t('m-apps:waybill.form.good_type.hint')}>
                      <BayanGoodTypeSelect
                        value={draft.goodTypeId}
                        onChange={value => setField('goodTypeId', value)}
                        narrowToItemType={draft.itemType}
                        narrowToBranchId={requirements.goodTypeBranchId}
                        placeholder={t('m-apps:waybill.form.good_type.placeholder')}
                      />
                    </Field>
                  </Section>
                ) : null}

                {requirements.loadPrice ? (
                  <Section title={t('m-apps:waybill.form.fare.title')}>
                    <Field hint={t('m-apps:waybill.form.fare.hint')}>
                      <PriceInput
                        value={draft.loadPrice}
                        onChange={value => setField('loadPrice', value)}
                        currency={currency}
                        priceType="before_tax"
                      />
                    </Field>
                  </Section>
                ) : null}

                {requirements.crNumber ? (
                  <Section title={t('m-apps:waybill.form.cr.title')}>
                    <Field hint={t('m-apps:waybill.form.cr.hint')}>
                      <Input
                        value={draft.crNumber}
                        onChange={event => setField('crNumber', event.target.value)}
                        placeholder={t('m-apps:waybill.form.cr.placeholder')}
                        inputMode="numeric"
                        dir="ltr"
                      />
                    </Field>
                  </Section>
                ) : null}

                {unfixableBlockers.length > 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center gap-2">
                      <AlertCircleIcon className="size-4 shrink-0 text-amber-600" />
                      <span className="text-[13px] font-[600] leading-[20px] text-amber-900">
                        {t('m-apps:waybill.form.elsewhere.title')}
                      </span>
                    </div>
                    <ul className="mt-2 space-y-1 ps-6">
                      {unfixableBlockers.map(blocker => (
                        <li
                          key={blocker.code}
                          className="text-[13px] leading-[20px] text-amber-800 before:me-2 before:content-['•']">
                          {getApiErrorMessage(blocker.code) ?? blocker.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {error ? (
                  <p className="rounded-lg bg-red-50 p-3 text-[13px] leading-[20px] text-red-700">
                    {error}
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {!isIssued ? (
            <div className="flex gap-3 border-t px-5 py-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={onClose}
                disabled={isSubmitting}>
                {t('c-main:crud_buttons.cancel')}
              </Button>
              <Button className="flex-1" onClick={() => void submit()} disabled={!canIssue}>
                {isSubmitting ? (
                  <>
                    <Spinner className="size-4 me-2" />
                    {t('m-apps:waybill.form.issuing')}
                  </>
                ) : (
                  t('m-apps:waybill.issue_now')
                )}
              </Button>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Both forms write straight to their own record, so the drawer has to ask
          for the blockers again to notice the fix. */}
      {order.driver?._id ? (
        <MemberEditModal
          isOpen={isDriverEditOpen}
          onClose={() => setIsDriverEditOpen(false)}
          memberId={order.driver._id}
          displayForUser="carrier"
          forceType="driver"
          onMemberSaved={() => {
            setIsDriverEditOpen(false);
            void refetchStatus();
          }}
        />
      ) : null}

      {order.vehicle?._id ? (
        <VehicleEditModal
          isOpen={isVehicleEditOpen}
          onClose={() => setIsVehicleEditOpen(false)}
          vehicleId={order.vehicle._id}
          onVehicleSaved={() => {
            setIsVehicleEditOpen(false);
            void refetchStatus();
          }}
        />
      ) : null}
    </>
  );
};

/** A titled group of inputs for one record. */
const Section: React.FC<{
  title: string;
  icon?: React.FC<{ className?: string }>;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      {Icon ? <Icon className="size-4 text-gray-400" /> : null}
      <h3 className="text-[13px] font-[600] uppercase leading-[20px] tracking-wide text-gray-500">
        {title}
      </h3>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

/**
 * One labelled input.
 *
 * The hint carries the Arabic field name Bayan itself uses (رقم السجل التجاري).
 * Whoever fills this in is copying off a document that says exactly that, so
 * naming the field the way the document does is what makes it unambiguous.
 */
const Field: React.FC<{
  label?: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    {label ? (
      <label className="block text-[13px] font-[500] leading-[20px] text-gray-700">{label}</label>
    ) : null}
    {children}
    {hint ? <p className="text-[12px] leading-[18px] text-gray-500">{hint}</p> : null}
  </div>
);

/** What was issued, once there is a document to point at. */
const IssuedSummary: React.FC<{
  /** `tripId` is a string now; older documents still hold a number. */
  waybill?: { tripId?: string | number; waybillIds?: number[]; issuingOrgName?: string };
}> = ({ waybill }) => {
  const { t } = useTranslation(['m-apps']);

  const rows = [
    waybill?.tripId
      ? { label: t('m-apps:waybill.trip_id'), value: String(waybill.tripId) }
      : undefined,
    waybill?.waybillIds?.length
      ? { label: t('m-apps:waybill.waybill_number'), value: waybill.waybillIds.join(', ') }
      : undefined,
    waybill?.issuingOrgName
      ? { label: t('m-apps:waybill.issued_by'), value: waybill.issuingOrgName }
      : undefined,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg bg-emerald-50 p-4">
        <CircleCheckIcon className="mt-0.5 size-5 shrink-0 text-emerald-600" />
        <p className="text-[13px] leading-[20px] text-emerald-900">
          {t('m-apps:waybill.form.issued_hint')}
        </p>
      </div>
      <div className="divide-y divide-gray-100">
        {rows.map(row => (
          <div key={row.label} className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-[13px] leading-[20px] text-gray-500">{row.label}</span>
            <span className="text-[13px] font-[500] leading-[20px] text-gray-900">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
